import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ElevatedButton from './buttons.component';
import { useTranslation } from 'react-i18next';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { Navigate } from 'react-router-dom';

const PaymentCard = ({ clientSecret }) => {
  // Get the lookup key for the price from the previous page redirect.
  const [paymentIntent, setPaymentIntent] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { t } = useTranslation(['seller_onboarding']);

  // Initialize an instance of stripe.
  const stripe = useStripe();
  const elements = useElements();

  // When the subscribe-form is submitted we do a few things:
  //
  //   1. Tokenize the payment method
  //   2. Create the subscription
  //   3. Handle any next actions like 3D Secure that are required for SCA.
  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      return '';
    }

    try {
      // Get a reference to a mounted CardElement. Elements knows how
      // to find your CardElement because there can only ever be one of
      // each type of element.
      setIsLoading(true);
      setErrorMessage('');

      const cardElement = elements.getElement(CardElement);

      const userData = JSON.parse(
        localStorage.getItem('tw_user_data') || JSON.stringify({ name: '' })
      );

      // Use card Element to tokenize payment details
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${userData?.first_name || ''} ${userData?.last_name || ''}`
          }
        }
      });

      console.log('cardElement, error, paymentIntent =', {
        cardElement,
        error,
        paymentIntent
      });

      if (error) {
        setIsLoading(false);
        setErrorMessage(error?.message);
        return;
      }
      setPaymentIntent(paymentIntent);
      window.location.href = `${process.env.REACT_APP_DASHBOARD_URL}/profile`;
    } catch (error) {
      console.log('cardElement error =', error);
      setErrorMessage(error?.toString());
    } finally {
      setIsLoading(false);
    }
  };

  if (paymentIntent && paymentIntent.status === 'succeeded') {
    return <Navigate to='/profile' replace={true} />;
  }

  const spinner = (
    <div role='status'>
      <svg
        aria-hidden='true'
        className='tw-w-8 tw-h-8 tw-mr-2 tw-text-gray-200 tw-animate-spin dark:tw-text-gray-600 tw-fill-blue-600'
        viewBox='0 0 100 101'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
          fill='currentColor'
        />
        <path
          d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
          fill='currentFill'
        />
      </svg>
      <span className='tw-sr-only'>Loading...</span>
    </div>
  );

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className='tw-w-full sm:tw-w-[440px] tw-bg-white tw-p-8 tw-rounded-md tw-shadow'
      >
        {errorMessage && (
          <div className='tw-text-sm tw-bg-red-200 tw-text-whites tw-p-2 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-mb-5 tw-space-x-4'>
            <p>{errorMessage}</p>
            <button onClick={() => setErrorMessage('')}>
              <XCircleIcon className='tw-w-5 tw-h-5' />
            </button>
          </div>
        )}

        <h6 className='tw-text-center tw-mb-5 tw-leading-6'>
          Your last payment was unsuccessful on TradeWinds MarketPlace please try again
          with different card details.
        </h6>

        <CardElement className='tw-px-2 tw-py-3 tw-rounded tw-shadow tw-border-2 tw-border-cyan-400' />

        <ElevatedButton
          type={isLoading ? 'button' : 'submit'}
          className='tw-mt-6 tw-w-full !tw-text-base !tw-capitalize'
          variant='success'
        >
          {isLoading ? spinner : t('submit')}
        </ElevatedButton>
      </form>
    </>
  );
};

export default PaymentCard;
